import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/states';
import { api, refreshSession } from '@/lib/api';
import { useAuth } from '@/lib/auth';

export function ChangePasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const mismatch = again.length > 0 && again !== next;
  const valid = cur && next.length >= 8 && next === again;

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await api.post('/me/password', { currentPassword: cur, newPassword: next });
      toast.success('Đã đổi mật khẩu');
      setCur(''); setNext(''); setAgain('');
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Không đổi được mật khẩu');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title="Đổi mật khẩu"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Hủy</Button>
          <Button onClick={submit} disabled={!valid || busy}>{busy && <Spinner />} Đổi mật khẩu</Button>
        </>
      }
    >
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
        <div><Label>Mật khẩu hiện tại</Label><Input type="password" autoFocus autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} /></div>
        <div><Label>Mật khẩu mới (tối thiểu 8 ký tự)</Label><Input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} /></div>
        <div>
          <Label>Nhập lại mật khẩu mới</Label>
          <Input type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} aria-invalid={mismatch} />
          {mismatch && <p className="mt-0.5 text-xs text-destructive">Hai lần nhập không khớp</p>}
        </div>
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

/**
 * P21 C4: tài khoản vừa cấp mật khẩu tạm (hoặc vừa đặt lại) phải đổi mật khẩu trước khi dùng.
 * Máy chủ bỏ cờ must_change_password khi đổi xong; tải lại phiên để vào thẳng ứng dụng.
 */
export function ForcedPasswordChange() {
  const { user, logout } = useAuth();
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const mismatch = again.length > 0 && again !== next;
  const valid = cur && next.length >= 8 && next === again;
  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await api.post('/me/password', { currentPassword: cur, newPassword: next });
      toast.success('Đã đổi mật khẩu');
      await refreshSession();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Không đổi được mật khẩu');
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="flex min-h-dvh items-center justify-center bg-background p-4">
      <form
        className="w-full max-w-sm space-y-3 rounded-lg border bg-card p-5 shadow-sm"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <h1 className="text-lg font-semibold">Đổi mật khẩu lần đầu</h1>
        <p className="text-sm text-muted-foreground">
          Chào {user?.fullName}. Tài khoản <b>{user?.username}</b> đang dùng mật khẩu tạm. Đặt mật khẩu mới của riêng bạn để tiếp tục.
        </p>
        <div><Label htmlFor="fp-cur">Mật khẩu tạm</Label><Input id="fp-cur" type="password" autoFocus autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} /></div>
        <div><Label htmlFor="fp-new">Mật khẩu mới (tối thiểu 8 ký tự)</Label><Input id="fp-new" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} /></div>
        <div>
          <Label htmlFor="fp-again">Nhập lại mật khẩu mới</Label>
          <Input id="fp-again" type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} aria-invalid={mismatch} />
          {mismatch && <p className="mt-0.5 text-xs text-destructive">Hai lần nhập không khớp</p>}
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={() => void logout()}>Đăng xuất</Button>
          <Button type="submit" className="flex-1" disabled={!valid || busy}>{busy && <Spinner />} Đổi mật khẩu</Button>
        </div>
      </form>
    </main>
  );
}
